import * as React from "react";
import {
  Header,
  Box,
  Grid,
  SpaceBetween,
  Spinner,
  Alert,
  Icon,
} from "@cloudscape-design/components";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { OrganizationDetails } from "../types/organizationTypes";
import organizationService from "../services/organizationService";
import MembersTable from "../components/organizations/MembersTable";
import "../styles/common.css";
import UserService from "../services/UserService";
import { Member } from "types/Members";
import { useBreadcrumbs } from "../contexts/BreadcrumbContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-regular-svg-icons";
import PERMISSIONS from "configs/Permissions";
import CardSummary from "components/shared/CardSummary";
import AppConfig from "configs/AppConfig";

const IndividualOrganization: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [organization, setOrganization] = useState<OrganizationDetails | null>(
    null
  );
  const [organizationMembers, setOrganizationMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectedUserId, setConnectedUserId] = useState<string>(
    UserService.getUserId() || ""
  );

  const fetchOrganizationData = async () => {
    if (!id) return;

    try {
      setLoading(true);

      const orgData = await organizationService.getOrganizationById(id);
      setOrganization(orgData);

      if (UserService.hasRole([PERMISSIONS.ORGANIZATIONS.VIEW_MEMBERS])) {
        const membersData = await organizationService.getOrganizationMembers(id);
        setOrganizationMembers(membersData?.members || []);
      }

      setError(null);
    } catch (error) {
      console.error("Error fetching organization:", error);
      setError("Failed to load organization details");
    } finally {
      setLoading(false);
    }
  };

  const handleSSOSetupClick = () => {
    if (organization) {
      navigate(`/organizations/${id}/sso-providers`, {
        state: { organization },
      });
    }
  }

  useEffect(() => {
    fetchOrganizationData();
  }, [id]);

  useEffect(() => {
    if (organization) {
      setBreadcrumbs([
        { text: AppConfig.APP_NAME, href: "/" },
        { text: "Organizations", href: "/organizations" },
        { text: organization.display_name, href: `/organizations/${id}` },
      ]);
    }

    // Cleanup when component unmounts
    return () => {
      setBreadcrumbs([]);
    };
  }, [id, organization, setBreadcrumbs]);

  const handleDomainSetupClick = () => {
    if (organization) {
      navigate(`/organizations/${id}/domains`, {
        state: { organization },
      });
    }
  };

  const handlePendingClick = ()=>{
    if(UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS])){
      navigate(`/organizations/${id}/invitations/pending`)
    }
  }

  const onInviteClick= ()=>{
    navigate(`/organizations/${id}/invite`, {
      state: { organization },
    })
  }


  return (
    <SpaceBetween size="xl">
      {loading && (
        <Box padding={{ horizontal: "m", vertical: "l" }}>
          <Spinner size="large" />
          <Box color="text-status-info" fontSize="heading-s">
            Loading organization details...
          </Box>
        </Box>
      )}

      {(error || !organization) && !loading && (
        <Box padding={{ horizontal: "m", vertical: "l" }}>
          <Alert type="error">{error || "Organization not found"}</Alert>
        </Box>
      )}

      {!loading && !error && organization && (
        <>
          <SpaceBetween size="xl">
            <div className="header-container">
              <Header variant="h2">
                <FontAwesomeIcon
                  icon={faBuilding}
                  className="building-icon"
                />{" "}
                {organization?.display_name || "Loading..."}
              </Header>
            </div>

            <Grid
              gridDefinition={[
                { colspan: { default: 12, xs: 4 } },
                { colspan: { default: 12, xs: 4 } },
                { colspan: { default: 12, xs: 4 } },
              ]}
            >
              
              {/* Members Card */}
              <CardSummary 
                icon={<Icon name="user-profile" size="medium" />} 
                stats={[{text: "Members", count: organization.membersCount }, {text: "Pending", count: organization.pendingMembers, action:handlePendingClick, clickable: UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS])}]} 
                text="Invite new members or remove members from the organization" 
                buttonText="Invite member" 
                buttonAction={onInviteClick} 
                buttonIcon="add-plus"
                disabledButton={!UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS])} 
              />

              {/* SSO Card */}
              <CardSummary 
                icon={<Icon name="key" size="medium" />} 
                stats={[{text: "Active SSO Connections", count: organization.ssoConnections}]} 
                text="Setup SSO connections as necessary for this organization" 
                buttonText="Setup SSO" 
                buttonAction={handleSSOSetupClick} 
                disabledButton={!UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_ORGANIZATIONS])}
              />
              
              {/* Domains Card */}
              <CardSummary 
                icon={<Icon name="globe" size="medium" />} 
                stats={[{text: "Domains", count: organization.domains}, {text: "Pending", count: organization.pendingDomains}]} 
                text="Setup associated domains and verify them to ensure full security" 
                buttonText="View domains" 
                buttonAction={handleDomainSetupClick} 
                disabledButton={!UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_ORGANIZATIONS])} 
              />

            </Grid>
          </SpaceBetween>
          {
            UserService.hasRole([PERMISSIONS.ORGANIZATIONS.VIEW_MEMBERS]) && (
              <MembersTable
                members={organizationMembers}
                connectedUserId={connectedUserId}
                onMemberDeleted={fetchOrganizationData}
              />
            )
          }
        </>
      )}
    </SpaceBetween>
  );
};

export default IndividualOrganization;
